import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Check, X } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  getGetDoclistQueryKey,
  useRenameDoclistSection,
} from "~/api/chat/chat";
import { getListCustomReportsByCaseQueryKey } from "~/api/custom-reports/custom-reports";
import { DoclistTreeItemSectionsItem } from "~/api/model";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";

interface DocListSectionInputProps {
  section: DoclistTreeItemSectionsItem;
  organizationId: string;
  projectId: string;
  updatedAt: number | undefined;
  onUpdatedAtChange: (updatedAt: number) => void;
  onDoclistOutdated: () => void;
  onCancel: () => void;
}

export const DocListSectionInput: FC<DocListSectionInputProps> = ({
  section,
  organizationId,
  projectId,
  updatedAt,
  onUpdatedAtChange,
  onDoclistOutdated,
  onCancel,
}) => {
  const [sectionName, setSectionName] = useState(section.name);
  const [inputTouched, setInputTouched] = useState(false);
  const formSectionRef = useRef<HTMLFormElement>(null);
  const queryClient = useQueryClient();
  const { mutateAsync: renameSection } = useRenameDoclistSection();

  const remaneSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    trackEvent({
      eventName: "app/doc_list/rename_section.submit",
    });
    if (!updatedAt) return;
    if (
      !sectionName.trim().length ||
      sectionName.trim() === section.name.trim()
    )
      return;
    try {
      const response = await renameSection({
        organizationId,
        projectId,
        sectionId: section.id,
        data: {
          title: sectionName.trim(),
          updatedAt: updatedAt,
        },
      });
      onUpdatedAtChange(response.updatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, projectId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, projectId),
      });
      onCancel();
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          onDoclistOutdated();
        }
      }
      logger.error(error);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      remaneSubmit(e as any);
    } else if (e.key === "Escape") {
      setInputTouched(false);
      onCancel();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        formSectionRef.current &&
        !formSectionRef.current.contains(event.target as Node)
      ) {
        setInputTouched(false);
        onCancel();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <form ref={formSectionRef} onSubmit={remaneSubmit}>
      <div className="flex items-center gap-2.5 bg-blue-50 border-black border border-opacity-30 pl-[9px] pr-[11px] py-[7px] rounded-md">
        <Input
          name="sectionName"
          autoFocus
          value={sectionName}
          onChange={(e) => {
            setInputTouched(true);
            setSectionName(e.target.value);
          }}
          onKeyDown={handleInputKeyDown}
          className="border-0 p-0 h-auto font-normal"
        />
        <div className="flex items-start gap-1 -mr-1">
          <Button
            type="submit"
            variant="outline"
            size="icon"
            className="w-6 h-6 rounded-sm"
          >
            <Check className="w-3 h-3 text-blue-600" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="w-6 h-6 rounded-sm"
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
          >
            <X className="w-3 h-3 text-red-600" />
          </Button>
        </div>
      </div>
      {!sectionName.trim().length && inputTouched && (
        <span className="text-sm text-destructive">
          <FormattedMessage
            id="docList.addCategory.empty"
            defaultMessage="The name can't be empty"
          />
        </span>
      )}
    </form>
  );
};
